import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ArrowLeft, Send, Loader2, CheckCircle2 } from "lucide-react";

/**
 * Ask for the email → backend generates a token and sends a WhatsApp reset link.
 * Always shows the "check your WhatsApp" message to avoid revealing account existence.
 */
export default function MemberForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email"); return; }
    setBusy(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/hub/forgot-password`,
        { email: email.trim() },
      );
      setSent(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not process request");
    } finally { setBusy(false); }
  };

  return (
    <section className="min-h-[calc(100vh-80px)] py-16 px-5" data-testid="member-forgot-page">
      <div className="max-w-md mx-auto">
        <Link to="/member/login" className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-white/50 hover:text-white mb-6">
          <ArrowLeft size={12} /> Back to sign-in
        </Link>

        <div className="font-mono text-xs uppercase tracking-[0.3em] brand-orange mb-3">// Forgot password</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter leading-none mb-3">Reset it fast.</h1>
        <p className="text-sm text-white/60 mb-8">
          Enter the email you use for HTP Sports Hub. We&apos;ll send a reset link on WhatsApp to the phone number on your account.
        </p>

        {sent ? (
          <div className="border border-green-500/40 bg-green-500/10 p-6 text-center" data-testid="forgot-sent">
            <CheckCircle2 className="mx-auto brand-blue" size={40} />
            <div className="font-display text-xl font-black uppercase mt-3">Check Your WhatsApp</div>
            <div className="text-sm text-white/70 mt-2">
              If we found an account for <b>{email}</b>, we&apos;ve sent a reset link to the WhatsApp number on file. Link expires in 30 minutes.
            </div>
            <button onClick={() => navigate("/member/login")} className="mt-6 bg-brand-orange text-black px-6 py-2.5 font-bold text-xs uppercase tracking-widest hover:bg-white">
              Back to sign-in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/12 bg-[#131A2B] p-6 space-y-4">
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">Email</div>
              <div className="flex items-center border border-white/20 focus-within:border-brand-orange bg-[#0B1120]">
                <Mail size={14} className="ml-3 text-white/40" />
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email" autoFocus placeholder="you@example.com"
                  className="w-full bg-transparent py-2.5 px-3 text-sm outline-none" />
              </div>
            </label>
            <button type="submit" disabled={busy}
              data-testid="forgot-submit"
              className="w-full bg-brand-orange text-black py-3 font-bold text-sm uppercase tracking-widest hover:bg-white disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {busy ? "Sending…" : "Send reset link on WhatsApp"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
